import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useSession, signOut } from '../../lib/auth'
import { get } from '../../lib/api'
import { useSite } from '../../store/site'
import Logo from '../Logo'
import SiteSwitcher from '../SiteSwitcher'
import { Banner, Drawer, Toaster } from '../ui'

// Sidebar groups per CONTRACT-V2 §6. Settings lives in the account block at the bottom.
//
// Every group carries a heading except Home, so each gap in the sidebar is
// explained by the label above it. Insights and Subscribers used to be groups of
// one with their heading suppressed, which left them floating between two full
// section gaps with nothing to account for the space.
const NAV_GROUPS = [
  { label: 'Overview', hideLabel: true, items: [{ to: '/app', end: true, label: 'Home' }] },
  {
    label: 'Popups',
    items: [
      { to: '/app/popups', label: 'All popups' },
      { to: '/app/popups/new', label: 'Create' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { to: '/app/analytics/funnel', label: 'Funnel' },
      { to: '/app/analytics/attribution', label: 'Attribution' },
      { to: '/app/analytics/quality', label: 'Subscriber quality' },
      { to: '/app/analytics/sources', label: 'Sources & cohorts' },
      { to: '/app/analytics/journeys', label: 'Journeys' },
      { to: '/app/analytics/ask', label: 'Ask' },
      { to: '/app/insights', label: 'Insights' },
    ],
  },
  { label: 'Audience', items: [{ to: '/app/subscribers', label: 'Subscribers' }] },
  {
    label: 'Setup',
    items: [
      { to: '/app/accounts', label: 'Klaviyo accounts' },
      { to: '/app/assets', label: 'Assets' },
      { to: '/app/developers', label: 'Claude Code & API' },
    ],
  },
]
const ALL = NAV_GROUPS.flatMap((g) => g.items)
function activeTo(pathname) {
  if (pathname === '/app/popups/new') return '/app/popups/new'
  let best = null
  for (const it of ALL) {
    const match = it.end ? pathname === it.to : pathname === it.to || pathname.startsWith(it.to + '/')
    if (match && (!best || it.to.length > best.length)) best = it.to
  }
  return best
}

const DEFAULT_SITE_KEY = 'pm-default-site'
export const getDefaultSiteId = () => { try { return localStorage.getItem(DEFAULT_SITE_KEY) || '' } catch { return '' } }
export const setDefaultSiteId = (id) => { try { id ? localStorage.setItem(DEFAULT_SITE_KEY, id) : localStorage.removeItem(DEFAULT_SITE_KEY) } catch { /* ignore */ } }

/** Fetch Klaviyo accounts into the store. Pages call this after creating/editing one. */
export async function refreshSites() {
  const r = await get('/api/sites')
  useSite.getState().setSites(r.sites ?? [])
  return r.sites ?? []
}

export default function AppShell() {
  const { data: session, isPending } = useSession()
  const [me, setMe] = useState(null)
  const [open, setOpen] = useState(false)
  const nav = useNavigate()
  const { pathname, search } = useLocation()
  const current = activeTo(pathname)
  const { siteId, sites, setSiteId, loaded: sitesLoaded } = useSite()
  const site = sites.find((s) => s.id === siteId) ?? null

  useEffect(() => {
    if (isPending) return
    if (!session) { nav(`/login?next=${encodeURIComponent(pathname + search)}`, { replace: true }); return }
    get('/api/me').then(setMe).catch(() => {})
    refreshSites().then((list) => {
      // First load in this browser tab: open on the default Klaviyo account, if one is set.
      try {
        if (!sessionStorage.getItem('pm-site-init')) {
          sessionStorage.setItem('pm-site-init', '1')
          const d = getDefaultSiteId()
          if (d && list.some((s) => s.id === d)) setSiteId(d)
        }
      } catch { /* ignore */ }
    }).catch(() => {})
  }, [isPending, session]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setOpen(false) }, [pathname])

  if (isPending || !session) {
    return (
      <div className="min-h-full flex items-center justify-center bg-surface">
        <Logo className="h-10 animate-pulse" />
      </div>
    )
  }

  const Sidebar = (
    <aside className="border-r border-border bg-white flex flex-col min-h-full">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <Link to="/app" aria-label="Home"><Logo className="h-7" alt="" /></Link>
        <button className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded text-ink-3 hover:text-ink hover:bg-surface" onClick={() => setOpen(false)} aria-label="Close menu">✕</button>
      </div>
      <div className="p-3 border-b border-border">
        <SiteSwitcher />
      </div>
      <nav className="p-3 space-y-4 flex-1 overflow-y-auto" aria-label="Main">
        {NAV_GROUPS.map((g) => (
          <div key={g.label}>
            {!g.hideLabel && <div className="px-3 text-[10px] font-bold tracking-[0.16em] uppercase text-ink-3 mb-1">{g.label}</div>}
            <ul className="space-y-0.5">
              {g.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={'flex items-center justify-between px-3 py-2 min-h-[40px] rounded text-sm font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 ' +
                      (item.to === current ? 'bg-red-tint text-red-dark' : 'text-ink-2 hover:bg-surface')}
                  >
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="text-[11px] text-ink-3">Signed in as</div>
        <div className="text-sm font-semibold text-ink truncate">{session.user.name || 'Account'}</div>
        <div className="text-xs text-ink-3 truncate">{session.user.email}</div>
        <NavLink
          to="/app/settings"
          className={({ isActive }) => 'flex items-center justify-center gap-2 w-full mt-3 px-3 py-2 min-h-[40px] rounded text-sm font-medium border transition-colors ' +
            (isActive ? 'bg-red-tint text-red-dark border-red/30' : 'text-ink-2 border-border hover:bg-surface')}
        >
          <span aria-hidden>⚙</span> Settings
        </NavLink>
        <button
          onClick={async () => { await signOut(); nav('/login', { replace: true }) }}
          className="btn-ghost text-xs w-full mt-2"
        >
          Sign out
        </button>
      </div>
    </aside>
  )

  // Only judge the account once this session has heard back from /api/sites: the
  // persisted copy can predate the key being connected, and saying "no Klaviyo
  // connection" about a connected account is worse than saying nothing for a moment.
  const failing = sitesLoaded && site && site.health === 'fail'
  const notConnected = sitesLoaded && site && !site.klaviyoConnected

  return (
    <div className="min-h-full grid grid-cols-1 md:grid-cols-[250px_1fr]">
      <div className="hidden md:block">{Sidebar}</div>
      <Drawer open={open} onClose={() => setOpen(false)} side="left" width="w-[280px] max-w-[88vw]">{Sidebar}</Drawer>
      <main className="bg-surface min-h-full min-w-0">
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-border px-3 h-14 flex items-center justify-between gap-2">
          <button className="inline-flex h-10 w-10 items-center justify-center rounded text-ink-2 hover:bg-surface text-lg" onClick={() => setOpen(true)} aria-label="Open menu">☰</button>
          <Link to="/app" aria-label="Home" className="flex-none"><Logo className="h-6" alt="" /></Link>
          <SiteSwitcher compact className="ml-auto" />
        </div>
        {(failing || notConnected) && (
          <div className="px-5 md:px-8 pt-4">
            <Banner tone={failing ? 'fail' : 'warn'}
              action={<Link to={`/app/accounts/${site.id}?tab=${notConnected ? 'klaviyo' : 'health'}`} className="text-xs font-semibold underline whitespace-nowrap">{notConnected ? 'Connect Klaviyo' : 'Open health checks'}</Link>}>
              <strong>{site.name}</strong>{notConnected ? ' has no Klaviyo connection. Signups are stored here and synced once you connect a private API key.' : ' has a failing Klaviyo health check, so signups may not be reaching Klaviyo.'}
            </Banner>
          </div>
        )}
        <Outlet context={{ me, session }} />
      </main>
      <Toaster />
    </div>
  )
}
